import React from 'react';
import { Badge, Button, Tooltip, Typography } from 'antd';
import { ReloadOutlined, WifiOutlined } from '@ant-design/icons';
import { useLanguage } from '../../contexts/LanguageContext';
import ChatSessionListItem from './ChatSessionListItem';

const { Text } = Typography;

/**
 * 左側 session 列表容器。
 * header 顯示「對話列表」標題 + 總 session 數 badge + 總未讀 badge。
 * isPolling=true 時顯示綠色「Live」小圓點。
 * getExtraInfo(session) 為選用函數，供 Inbox 模式傳入 workflow/task 補充資訊。
 */
const ChatSessionList = ({
  sessions,
  activeSessionId,
  interventionMessages = {},
  unreadCounts = {},
  newSessionIds = new Set(),
  onSelect,
  onRefresh,
  width = 260,
  getExtraInfo,
  isPolling = false
}) => {
  const { t } = useLanguage();
  const totalUnread = Object.values(unreadCounts).reduce((acc, n) => acc + n, 0);

  return (
    <div
      style={{
        width,
        borderRight: '1px solid #f0f0f0',
        display: 'flex',
        flexDirection: 'column',
        background: '#fafafa',
        flexShrink: 0,
        flex: 1,
        minHeight: 0
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '8px 12px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#fff',
          gap: 6
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Text strong style={{ fontSize: 13 }}>
            {t('workflowMonitor.chatBotSessions') || '對話列表'}
          </Text>
          {/* 總未讀數徽章 */}
          {totalUnread > 0 && (
            <Badge
              count={totalUnread}
              style={{ backgroundColor: '#ff4d4f', fontSize: 11 }}
            />
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Live 狀態燈 */}
          {isPolling && (
            <Tooltip title={t('chatBotInbox.pollingActive') || '即時更新中'}>
              <WifiOutlined style={{ color: '#52c41a', fontSize: 13 }} />
            </Tooltip>
          )}
          {/* 總 session 數 */}
          <Badge count={sessions.length} showZero color="#1677ff" style={{ fontSize: 11 }} />
        </div>
      </div>

      {/* 列表 */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {sessions.map((session) => (
          <ChatSessionListItem
            key={session.id}
            session={session}
            isActive={session.id === activeSessionId}
            interventionCount={(interventionMessages[session.id] || []).length}
            unreadCount={unreadCounts[session.id] || 0}
            isNew={newSessionIds.has(session.id)}
            onClick={() => onSelect(session.id)}
            extraInfo={getExtraInfo ? getExtraInfo(session) : undefined}
          />
        ))}
      </div>

      {/* 底部重新整理 */}
      <div style={{ padding: 8, borderTop: '1px solid #f0f0f0', background: '#fff' }}>
        <Button size="small" block icon={<ReloadOutlined />} onClick={onRefresh}>
          {t('workflowMonitor.refresh') || '重新整理'}
        </Button>
      </div>
    </div>
  );
};

export default ChatSessionList;
